import {
  DashboardData,
  DashboardPeriodOption,
  DashboardSeries,
  TimeSeriesPoint,
} from '../../models/interfaces/dashboard/dashboard.interface';

const buildSeries = (baseValue: number, variance: number): TimeSeriesPoint[] => {
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

  return months.map((timestamp, index) => ({
    timestamp,
    value: Math.round(baseValue + Math.sin(index) * variance + index * (variance / 4)),
  }));
};

export const dashboardSeriesMock: DashboardSeries = {
  cpu: buildSeries(42, 8),
  latency: buildSeries(120, 25),
  errorRate: buildSeries(2.4, 0.8),
  engagement: buildSeries(64, 12),
  courses: buildSeries(38, 10),
};

export const dashboardPeriodOptionsMock: DashboardPeriodOption[] = [
  { label: 'Janeiro 2026', value: '2026-01', month: 0, year: 2026 },
  { label: 'Fevereiro 2026', value: '2026-02', month: 1, year: 2026 },
  { label: 'Março 2026', value: '2026-03', month: 2, year: 2026 },
  { label: 'Ano 2026', value: '2026-year', month: -1, year: 2026 },
  { label: 'Ano 2025', value: '2025-year', month: -1, year: 2025 },
];

export const dashboardDataMock: DashboardData = {
  highlight: {
    icon: 'visibility',
    title: 'Aulas assistidas',
    monthValue: 346,
    yearValue: 713,
  },
  topMetrics: [
    {
      icon: 'trending_up',
      title: 'Curso mais acessado',
      period: 'Mês',
      value: 'A Arte da Gestão de Pessoas',
      variant: 'success',
    },
    {
      icon: 'trending_down',
      title: 'Curso menos acessado',
      period: 'Mês',
      value: 'Finanças Inteligentes',
      variant: 'danger',
    },
    {
      icon: 'trending_up',
      title: 'Curso mais acessado',
      period: 'Ano',
      value: 'Código de Ética',
      variant: 'success',
    },
    {
      icon: 'trending_down',
      title: 'Curso menos acessado',
      period: 'Ano',
      value: 'Comunicação Eficaz e Não Violenta',
      variant: 'danger',
    },
  ],
  engagement: {
    donuts: [
      {
        icon: 'person',
        title: 'Usuários ativos diários (DAU)',
        percentage: 12,
        absoluteValue: 342,
        absoluteLabel: 'usuários',
        trends: [
          { label: 'Fevereiro', percentage: 5, isPositive: true },
          { label: 'Janeiro', percentage: 3, isPositive: false },
        ],
      },
      {
        icon: 'person',
        title: 'Usuários ativos mensais (MAU)',
        percentage: 48,
        absoluteValue: 1368,
        absoluteLabel: 'usuários',
        trends: [
          { label: 'Fevereiro', percentage: 5, isPositive: true },
          { label: 'Janeiro', percentage: 3, isPositive: false },
        ],
      },
      {
        icon: 'pause_circle',
        title: 'Usuários inativos a mais de 30 dias',
        percentage: 31,
        absoluteValue: 562,
        absoluteLabel: 'usuários',
        trends: [
          { label: 'Fevereiro', percentage: 5, isPositive: true },
          { label: 'Janeiro', percentage: 3, isPositive: false },
        ],
      },
    ],
    progress: [
      {
        icon: 'login',
        title: 'Taxa de Login',
        percentage: 64,
        absoluteValue: 1820,
        absoluteLabel: 'usuários',
        trends: [
          { label: 'Fevereiro', percentage: 5, isPositive: true },
          { label: 'Janeiro', percentage: 3, isPositive: false },
        ],
      },
      {
        icon: 'power_settings_new',
        title: 'Taxa de Ativação',
        percentage: 64,
        absoluteValue: 1820,
        absoluteLabel: 'usuários',
        trends: [
          { label: 'Fevereiro', percentage: 5, isPositive: true },
          { label: 'Janeiro', percentage: 3, isPositive: false },
        ],
      },
    ],
  },
  learning: {
    completionRate: {
      icon: 'gps_fixed',
      title: 'Taxa média de conclusão',
      percentage: 78,
      absoluteValue: 0,
      absoluteLabel: '',
      trends: [
        { label: 'Fevereiro', percentage: 5, isPositive: true },
        { label: 'Janeiro', percentage: 3, isPositive: false },
      ],
    },
    averageProgress: {
      icon: 'donut_large',
      title: 'Progresso médio (Cursos em andamento)',
      percentage: 64,
      absoluteValue: 120,
      absoluteLabel: 'cursos ativos',
      trends: [
        { label: 'Fevereiro', percentage: 5, isPositive: true },
        { label: 'Janeiro', percentage: 3, isPositive: false },
      ],
    },
    averageCompletionTime: {
      title: 'Tempo médio de conclusão',
      value: '18 dias',
      icon: 'schedule',
      displayIcon: 'calendar_month',
    },
  },
  effectiveness: {
    averageGrade: {
      title: 'Nota média dos cursos',
      value: 9.0,
      maxValue: 10,
      icon: 'description',
    },
    certificatesIssued: {
      title: 'Certificados emitidos',
      value: 1245,
      icon: 'school',
      trendLabel: '+ 120 este mês',
    },
  },
  rankings: {
    mostWatchedLessons: [
      { position: 1, name: 'Engenharia da vida', value: 12, maxValue: 12, isPercentage: false },
      { position: 2, name: 'Políticas Públicas', value: 6, maxValue: 12, isPercentage: false },
      { position: 3, name: 'Estilos de liderança', value: 4, maxValue: 12, isPercentage: false },
      { position: 4, name: 'A Jornada Humana', value: 2, maxValue: 12, isPercentage: false },
      { position: 5, name: 'Energia Limpa', value: 2, maxValue: 12, isPercentage: false },
    ],
    mostAccessedCourses: [
      { position: 1, name: 'Engenharia da vida', value: 12, maxValue: 12, isPercentage: false },
      { position: 2, name: 'A importância da liderança consciente', value: 9, maxValue: 12, isPercentage: false },
      { position: 3, name: 'Políticas Públicas', value: 6, maxValue: 12, isPercentage: false },
      { position: 4, name: 'Estilos de liderança', value: 4, maxValue: 12, isPercentage: false },
      { position: 5, name: 'A Jornada Humana', value: 2, maxValue: 12, isPercentage: false },
    ],
    highestCompletionCourses: [
      { position: 1, name: 'A Arte da Gestão de Pessoas', value: 80, maxValue: 100, isPercentage: true },
      { position: 2, name: 'A liderança no século XXI', value: 40, maxValue: 100, isPercentage: true },
      { position: 3, name: 'Código de Ética', value: 35, maxValue: 100, isPercentage: true },
      { position: 4, name: 'Comunicação Eficaz', value: 28, maxValue: 100, isPercentage: true },
      { position: 5, name: 'Finanças Inteligentes', value: 15, maxValue: 100, isPercentage: true },
    ],
  },
  series: dashboardSeriesMock,
  periodOptions: dashboardPeriodOptionsMock,
};
